import React from 'react';
import { MenuItem, TextField } from '@mui/material';
import { AgronodTypography } from '@agronod/mui-components';
import {
  PERIOD_MONTHS_MAX,
  PERIOD_MONTHS_MIN,
  describeControlPeriod,
  resolveControlPeriod,
  type ControlPeriod,
  type ControlPeriodConfig,
} from '@shared/control-period';
import { toDateOnlyString } from '@/utils/format';

type PeriodChoice = 'before' | 'own' | 'rolling';

/**
 * The period as the admin previews read it: "kontrollperiod 2025" for a
 * calendar year, "perioden A – B" for a rolling span. Shared with the
 * requirement editor's year-window preview.
 */
export function previewPhrase(period: ControlPeriod): string {
  return period.mode === 'ROLLING_MONTHS'
    ? describeControlPeriod(period)
    : `kontrollperiod ${period.mainYear}`;
}

interface ControlPeriodSelectProps {
  value: ControlPeriodConfig;
  onChange: (next: ControlPeriodConfig) => void;
  /** Error for the months field (rolling mode), set by the form's validation. */
  monthsError?: string | undefined;
  disabled?: boolean;
}

function choiceOf(config: ControlPeriodConfig): PeriodChoice {
  if (config.periodMode === 'ROLLING_MONTHS') return 'rolling';
  return config.periodYearOffset === 0 ? 'own' : 'before';
}

/**
 * Validation message for the months field, or undefined when the config can be
 * saved: rolling mode needs a whole number of months within the API's bounds.
 */
export function controlPeriodMonthsError(config: ControlPeriodConfig): string | undefined {
  if (config.periodMode !== 'ROLLING_MONTHS') return undefined;
  if (config.periodMonths === null || Number.isNaN(config.periodMonths)) return 'Ange antal månader';
  if (config.periodMonths < PERIOD_MONTHS_MIN || config.periodMonths > PERIOD_MONTHS_MAX) {
    return `Ange ${PERIOD_MONTHS_MIN}–${PERIOD_MONTHS_MAX} månader`;
  }
  return undefined;
}

/**
 * The wire format for a period config: months are a number for rolling and an
 * explicit null for calendar mode, so a stale value is cleared server-side.
 */
export function controlPeriodPayload(config: ControlPeriodConfig): ControlPeriodConfig {
  return {
    periodMode: config.periodMode,
    periodYearOffset: config.periodYearOffset,
    periodMonths: config.periodMode === 'ROLLING_MONTHS' ? config.periodMonths : null,
  };
}

/**
 * "Kontrollperiod" picker shared by the create and edit control type forms
 * (AG-298): how a control's period is anchored to its date. Shows a live
 * preview resolved with the same helper the backend uses, from today's date.
 */
const ControlPeriodSelect: React.FC<ControlPeriodSelectProps> = ({
  value,
  onChange,
  monthsError,
  disabled = false,
}) => {
  const choice = choiceOf(value);
  const today = toDateOnlyString(new Date());
  // A type migrated with an offset other than -1/0 keeps working; show it as such.
  const customOffset =
    value.periodMode === 'CALENDAR_YEAR' && value.periodYearOffset < -1 ? value.periodYearOffset : null;

  const handleChoice = (next: string) => {
    if (next === 'rolling') {
      onChange({ ...value, periodMode: 'ROLLING_MONTHS' });
    } else if (next === 'own') {
      onChange({ periodMode: 'CALENDAR_YEAR', periodYearOffset: 0, periodMonths: null });
    } else if (next === 'before') {
      onChange({ periodMode: 'CALENDAR_YEAR', periodYearOffset: -1, periodMonths: null });
    }
  };

  const canPreview = controlPeriodMonthsError(value) === undefined;
  const preview = canPreview
    ? `Kontroll ${today} → ${previewPhrase(resolveControlPeriod(value, today, today))}`
    : null;

  return (
    <>
      <TextField
        select
        fullWidth
        label="Kontrollperiod"
        value={customOffset !== null ? String(customOffset) : choice}
        onChange={(e) => handleChoice(e.target.value)}
        disabled={disabled}
        helperText="Styr vilket år eller vilken period kontrollen bedömer, och vilka år dokumenten föreslås för. Perioden räknas från kontrolldatumet, så typen fungerar för kommande år utan ändring."
      >
        <MenuItem value="before">Kalenderåret före kontrollen</MenuItem>
        <MenuItem value="own">Kontrollens eget år</MenuItem>
        <MenuItem value="rolling">Rullande antal månader bakåt</MenuItem>
        {customOffset !== null && (
          <MenuItem value={String(customOffset)}>{`Kalenderåret ${-customOffset} år före kontrollen`}</MenuItem>
        )}
      </TextField>

      {value.periodMode === 'ROLLING_MONTHS' && (
        <TextField
          type="number"
          label="Antal månader"
          required
          disabled={disabled}
          value={value.periodMonths ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            onChange({ ...value, periodMonths: raw === '' ? null : parseInt(raw, 10) });
          }}
          error={!!monthsError}
          helperText={monthsError}
          slotProps={{ htmlInput: { min: PERIOD_MONTHS_MIN, max: PERIOD_MONTHS_MAX } }}
          sx={{ mt: 2, width: 200 }}
        />
      )}

      {preview && (
        <AgronodTypography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          {preview}
        </AgronodTypography>
      )}
    </>
  );
};

export default ControlPeriodSelect;
